/**
 * InspectionReportBar - Situação e download do PDF completo da vistoria
 *
 * Exibido apenas em vistorias aprovadas/reprovadas. O PDF é gerado automaticamente
 * na conclusão; "Gerar PDF" cobre vistorias concluídas antes deste recurso e falhas.
 *
 * @version 1.1.0
 * RELEASE: 2026-09-27 v1.1.0 - Adiciona visualização interna do PDF pronto.
 * RELEASE: 2026-09-27 v1.0.0 - Versão inicial.
 */
import { useState } from 'react';
import { Download, Eye, FileText, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StatusDotLabel } from '@/components/ui/status-dot-label';
import {
  downloadChecklistInstanceReport, useChecklistInstanceReport, useRequestChecklistInstanceReport,
} from '@/hooks/useChecklistInstanceReport';
import { InspectionReportPreviewDialog } from './InspectionReportPreviewDialog';

interface InspectionReportBarProps {
  instanceId: string;
  title?: string;
}

export function InspectionReportBar({ instanceId, title }: InspectionReportBarProps) {
  const { data, isLoading } = useChecklistInstanceReport(instanceId);
  const request = useRequestChecklistInstanceReport(instanceId);
  const [busy, setBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  if (isLoading) return null;

  const label = !data ? 'PDF da vistoria não gerado'
    : { queued: 'PDF na fila', processing: 'Gerando PDF da vistoria', ready: `PDF da vistoria pronto (v${data.version})`, failed: 'Falha ao gerar PDF' }[data.status];
  const tone = data?.status === 'ready' ? 'success' : data?.status === 'failed' ? 'danger' : data ? 'info' : 'neutral';
  const canRequest = !data || data.status === 'failed';

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-card px-3 py-2 text-xs">
      <FileText className="h-4 w-4 text-primary" />
      <StatusDotLabel tone={tone as any} label={label} />
      <div className="ml-auto flex gap-2">
        {canRequest && (
          <Button size="sm" variant="outline" className="h-7 text-xs" disabled={request.isPending} onClick={() => request.mutate()}>
            {request.isPending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-1 h-3.5 w-3.5" />}
            {data ? 'Tentar novamente' : 'Gerar PDF'}
          </Button>
        )}
        {data?.status === 'ready' && (
          <>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setPreviewOpen(true)}>
              <Eye className="mr-1 h-3.5 w-3.5" />Visualizar PDF
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy}
              onClick={async () => { setBusy(true); await downloadChecklistInstanceReport(instanceId); setBusy(false); }}>
              {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Download className="mr-1 h-3.5 w-3.5" />}Baixar PDF da vistoria
            </Button>
          </>
        )}
      </div>
      <InspectionReportPreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        instanceId={instanceId}
        title={title}
      />
    </div>
  );
}
